import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";

// Section "Articles liés" — maillage interne SEO.
// Affiche jusqu'à 3 articles publiés (priorité à la même catégorie),
// en <Link> crawlables vers /article/:slug.
export default function RelatedArticles({ currentSlug, category }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await base44.entities.BlogArticle.filter(
          { status: "published" },
          "-created_date",
          12
        );
        if (cancelled) return;
        const others = (list || []).filter((a) => a.slug !== currentSlug);
        // Priorité à la même catégorie, complété par les plus récents
        const sameCat = others.filter((a) => a.category && a.category === category);
        const merged = [];
        const seen = new Set();
        for (const a of sameCat) {
          if (!seen.has(a.slug)) { merged.push(a); seen.add(a.slug); }
          if (merged.length >= 3) break;
        }
        for (const a of others) {
          if (merged.length >= 3) break;
          if (!seen.has(a.slug)) { merged.push(a); seen.add(a.slug); }
        }
        setArticles(merged.slice(0, 3));
      } catch {
        // silencieux — la section ne s'affiche simplement pas
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentSlug, category]);

  if (loading || articles.length === 0) return null;

  return (
    <section className="mt-16 pt-10 border-t border-[#001D31]/10">
      <h2 className="text-xl md:text-2xl font-semibold mb-6 [font-family:'Playfair_Display',_serif] text-[#001D31]">
        Articles liés
      </h2>
      <div className="grid gap-6 md:grid-cols-3">
        {articles.map((a) => (
          <Link
            key={a.id}
            to={`/article/${a.slug}`}
            className="group block rounded-2xl overflow-hidden border border-[#001D31]/10 bg-white hover:border-[#0985D8]/40 transition-colors"
          >
            {a.cover_image_url && (
              <div className="aspect-[16/9] overflow-hidden">
                <Image
                  src={a.cover_image_url}
                  alt={a.title}
                  fittingType="cover"
                  className="w-full h-full object-center transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            )}
            <div className="p-5">
              {a.category && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-[#0985D8]/40 text-[#0985D8] mb-3 inline-block">
                  {a.category}
                </span>
              )}
              <h3 className="text-sm font-semibold text-[#001D31] leading-snug mb-2 line-clamp-2 [font-family:'Poppins',_sans-serif]">
                {a.title}
              </h3>
              {a.excerpt && (
                <p className="text-xs text-[#8896B0] line-clamp-2">{a.excerpt}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}